import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../Components/Seo.jsx";
import PageHeader from "../Components/PageHeader.jsx";
import api from "../config/axiosConfig.jsx";
import defaultImg from "../assets/Images/event-default.jpg";
import { buildEventPath } from "../utils/eventSlug.js";
import { eventImage } from "../seo/schema.js";
import { ORG } from "../seo/site.js";
import { langOf } from "../utils/lang.js";

const PATH = "/events/";

// Same literal-digits reading as EventDetails.jsx (no timezone shift).
const formatEventDate = (dateString) => {
  const date = new Date(dateString);
  if (!dateString || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await api.get("/website/articles/type/Event/status/active");
        if (response.data.success && response.data.data?.articles) {
          setEvents(response.data.data.articles);
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <>
      <Seo path={PATH} />
      <PageHeader
        title="Events and News"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house" },
          { label: "Events", active: true },
        ]}
      />

      <section className="py-5">
        <div className="container py-lg-4">
          <p className="fs-5 mb-4 col-lg-9">
            Cultural celebrations, performances and community gatherings at
            Western Sydney Tamil Study Centre.
          </p>

          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading events...</span>
              </div>
            </div>
          )}

          {!loading && (error || events.length === 0) && (
            <div className="bg-white border rounded-4 border-bottom-left p-4">
              <h2 className="fs-4 fw-bold">
                {error ? "We couldn't load events right now" : "No upcoming events right now"}
              </h2>
              <p className="text-muted mb-0">
                {error ? "Please try again shortly. " : "New events are announced here and on our "}
                {!error && (
                  <a
                    href={ORG.sameAs[0]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="red-text fw-semibold"
                  >
                    YouTube channel
                    <span className="visually-hidden"> (opens in a new tab)</span>
                  </a>
                )}
                {!error && ". "}
                For questions, email{" "}
                <a href={`mailto:${ORG.email}`} className="red-text fw-semibold">
                  {ORG.email}
                </a>
                .
              </p>
            </div>
          )}

          {!loading && events.length > 0 && (
            <div className="row g-4">
              {events.map((event) => (
                <div className="col-md-6 col-lg-4" key={event.aid}>
                  <article className="card h-100 border rounded-4 border-bottom-left overflow-hidden">
                    <img
                      src={eventImage(event) || defaultImg}
                      alt=""
                      className="card-img-top object-fit-cover"
                      style={{ height: 220 }}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="card-body p-4 d-flex flex-column">
                      {event.event_venue?.start_date && (
                        <p className="small fw-bold red-text text-uppercase mb-2">
                          {formatEventDate(event.event_venue.start_date)}
                        </p>
                      )}
                      <h2 className="fs-5 fw-bold mb-2" lang={langOf(event.article_title)}>
                        <Link
                          to={buildEventPath(event)}
                          className="text-dark text-decoration-none stretched-link"
                        >
                          {event.article_title}
                        </Link>
                      </h2>
                      {event.event_venue?.venue && (
                        <p className="small text-muted mb-2">
                          <i className="bi bi-geo-alt me-1" aria-hidden="true"></i>
                          {event.event_venue.venue}
                        </p>
                      )}
                      {event.article_shortdesc && (
                        <p className="text-muted mb-0">{event.article_shortdesc}</p>
                      )}
                    </div>
                  </article>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Events;
